import React from 'react';
import { ChevronRight, TrendingUp, Wrench, Users, RefreshCw, Calendar, Clock, Bike } from 'lucide-react';
import { useLicense } from '../contexts/LicenseContext';
import { vtToHM, fmtCHF, fmtDate, PIPELINE_STAGES } from '../utils';

/**
 * Dashboard adaptatif selon les modules de la licence active
 * - SAV Atelier: OT, Planning, Charge techniciens, VT
 * - CRM Vente: Pipeline, Leads, CA, Essais
 * - Gestion Personnel: Équipe, Absences
 * - Bexio Sync: Statut sync, dernières factures
 */
const AdaptiveDashboard = ({ 
  stats, 
  ots, 
  leads, 
  techniciens, 
  clients, 
  motos, 
  absences,
  bexioStatus,
  testRides,
  setView 
}) => {
  const { hasModule, license } = useLicense();
  
  const otsProvisoires = ots.filter(o => o.statut === 'provisoire');
  const otsEnCours = ots.filter(o => o.statut === 'en_cours');
  const leadsActifs = leads.filter(l => !['gagne', 'perdu'].includes(l.statut));
  const essaisAujourdhui = (testRides || []).filter(tr => {
    const today = new Date().toISOString().split('T')[0];
    return tr.date_rdv?.startsWith(today);
  });

  const getClient = (id) => clients.find(c => c.id === id);
  const getMoto = (id) => motos.find(m => m.id === id);

  // Calculer les stats en fonction des modules actifs
  const activeModules = license?.modules || [];
  const hasSAV = hasModule('sav-atelier');
  const hasCRM = hasModule('crm-vente');
  const hasPersonnel = hasModule('gestion-personnel');
  const hasBexio = hasModule('bexio-sync');

  // Message si aucun module
  if (activeModules.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16" data-testid="dashboard-no-license">
        <div className="text-6xl mb-4">🔒</div>
        <h2 className="text-xl font-bold mb-2">Aucune licence active</h2>
        <p className="text-[var(--text3)] mb-6">Activez une licence pour accéder aux modules</p>
        <button className="btn btn-primary" onClick={() => setView('licenses')}>
          Gérer les licences
        </button>
      </div>
    );
  }

  return (
    <div data-testid="adaptive-dashboard">
      {/* Stats Cards - Adaptatifs selon modules */}
      <div className="stats-grid">
        {hasSAV && (
          <>
            <div className="stat-card" data-testid="stat-vt-planifies">
              <div className="stat-label">VT semaine planifiés</div>
              <div className="stat-value">{stats?.vt_planifies_actifs || 0}</div>
              <div className="stat-sub">{vtToHM(stats?.vt_planifies_actifs || 0)} de main d'œuvre</div>
            </div>
            <div className="stat-card" data-testid="stat-ot-confirmer">
              <div className="stat-label">OT à confirmer</div>
              <div className="stat-value" style={{ color: stats?.ot_provisoires > 0 ? 'var(--yellow)' : 'inherit' }}>
                {stats?.ot_provisoires || 0}
              </div>
              <div className="stat-sub">Offres Bexio en attente</div>
            </div>
          </>
        )}
        {hasCRM && (
          <>
            <div className="stat-card" data-testid="stat-pipeline">
              <div className="stat-label">Pipeline vente</div>
              <div className="stat-value">{fmtCHF(stats?.pipeline_total || 0)}</div>
              <div className="stat-sub">{stats?.leads_actifs || 0} leads actifs</div>
            </div>
            <div className="stat-card" data-testid="stat-essais">
              <div className="stat-label">Essais aujourd'hui</div>
              <div className="stat-value" style={{ color: essaisAujourdhui.length > 0 ? 'var(--green)' : 'inherit' }}>
                {essaisAujourdhui.length}
              </div>
              <div className="stat-sub">Test rides programmés</div>
            </div>
          </>
        )}
        {hasSAV && !hasCRM && (
          <div className="stat-card" data-testid="stat-occupation">
            <div className="stat-label">Taux occupation</div>
            <div className="stat-value">{stats?.taux_occupation || 0}%</div>
            <div className="stat-sub">Semaine courante</div>
          </div>
        )}
        {hasPersonnel && (
          <div className="stat-card" data-testid="stat-absences">
            <div className="stat-label">Absences cette semaine</div>
            <div className="stat-value">
              {(absences || []).filter(a => {
                const now = new Date();
                const start = new Date(a.date_debut);
                const end = new Date(a.date_fin);
                return start <= now && end >= now;
              }).length}
            </div>
            <div className="stat-sub">Techniciens absents</div>
          </div>
        )}
      </div>

      <div className="grid-2">
        {/* ============ SAV ATELIER WIDGETS ============ */}
        {hasSAV && (
          <>
            {/* OT Provisoires */}
            <div className="card">
              <div className="card-header">
                <span className="card-title flex items-center gap-2">
                  <Wrench size={16} style={{ color: 'var(--accent)' }} />
                  OT provisoires — à confirmer
                </span>
                <button className="btn btn-ghost btn-sm" onClick={() => setView('ot')}>
                  Tous les OT <ChevronRight size={14} />
                </button>
              </div>
              <div className="card-body">
                {otsProvisoires.length === 0 ? (
                  <p className="text-[var(--text3)] text-sm text-center py-4">Aucun OT provisoire ✓</p>
                ) : (
                  otsProvisoires.slice(0, 4).map(ot => {
                    const client = getClient(ot.client_id);
                    const moto = getMoto(ot.moto_id);
                    return (
                      <div key={ot.id} className="ot-row provisoire" data-testid={`ot-row-${ot.id}`}>
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <span className="font-mono text-sm px-2 py-0.5 rounded bg-[var(--orange)]/20 text-[var(--orange)]">
                              {ot.reference}
                            </span>
                          </div>
                          <span className="badge badge-yellow">Provisoire</span>
                        </div>
                        <div className="text-sm text-[var(--text2)]">
                          {client?.nom} — {moto?.modele}
                        </div>
                        <div className="flex justify-between items-center mt-2">
                          <span className="text-xs text-[var(--text3)]">{ot.vt_vendus} VT · {vtToHM(ot.vt_vendus)}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Charge Atelier */}
            <div className="card">
              <div className="card-header">
                <span className="card-title flex items-center gap-2">
                  <Clock size={16} style={{ color: 'var(--blue)' }} />
                  Charge atelier — Aujourd'hui
                </span>
                <button className="btn btn-ghost btn-sm" onClick={() => setView('planning')}>
                  Planning <ChevronRight size={14} />
                </button>
              </div>
              <div className="card-body">
                {techniciens.filter(t => t.role === 'technicien').slice(0, 5).map(tech => {
                  // Calculate real charge from OTs assigned today
                  const todayOTs = ots.filter(o => 
                    o.technicien_id === tech.id && 
                    o.date_planifiee === new Date().toISOString().split('T')[0]
                  );
                  const vtPlanifies = todayOTs.reduce((sum, o) => sum + (o.vt_planifies || 0), 0);
                  const capacite = tech.vt_jour_max || 96;
                  const pct = Math.min(Math.round((vtPlanifies / capacite) * 100), 100);
                  const color = pct > 85 ? 'var(--red)' : pct > 60 ? 'var(--yellow)' : 'var(--green)';
                  
                  return (
                    <div key={tech.id} className="flex items-center gap-3 mb-3">
                      <div className="tech-avatar" style={{ background: tech.couleur_hex }}>
                        {tech.initiales}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between text-sm">
                          <span>{tech.prenom}</span>
                          <span style={{ color }}>{pct}%</span>
                        </div>
                        <div className="charge-bar mt-1">
                          <div className="charge-fill" style={{ width: `${pct}%`, background: color }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
                {techniciens.filter(t => t.role === 'technicien').length === 0 && (
                  <p className="text-[var(--text3)] text-sm text-center py-4">Aucun technicien</p>
                )}
              </div>
            </div>
          </>
        )}

        {/* ============ CRM VENTE WIDGETS ============ */}
        {hasCRM && (
          <>
            {/* Pipeline Leads */}
            <div className="card">
              <div className="card-header">
                <span className="card-title flex items-center gap-2">
                  <TrendingUp size={16} style={{ color: 'var(--green)' }} />
                  Pipeline — leads actifs
                </span>
                <button className="btn btn-ghost btn-sm" onClick={() => setView('pipeline')}>
                  Complet <ChevronRight size={14} />
                </button>
              </div>
              <div className="card-body">
                {leadsActifs.length === 0 ? (
                  <p className="text-[var(--text3)] text-sm text-center py-4">Aucun lead actif</p>
                ) : (
                  <div className="space-y-2">
                    {leadsActifs.slice(0, 4).map(lead => {
                      const client = lead.client_id ? getClient(lead.client_id) : null;
                      const stage = PIPELINE_STAGES.find(s => s.key === lead.statut);
                      return (
                        <div key={lead.id} className="p-3 rounded-lg bg-[var(--bg2)] border border-[var(--border)]">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-medium text-sm">{lead.moto_libelle}</div>
                              <div className="text-xs text-[var(--text3)]">
                                {client ? `${client.prenom} ${client.nom}` : lead.prospect_nom}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-mono text-sm">{fmtCHF(lead.prix_offert)}</div>
                              <span className="badge text-xs" style={{ 
                                background: `${stage?.color}22`, 
                                color: stage?.color 
                              }}>
                                {stage?.label}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Essais du jour */}
            <div className="card">
              <div className="card-header">
                <span className="card-title flex items-center gap-2">
                  <Bike size={16} style={{ color: 'var(--accent)' }} />
                  Essais aujourd'hui
                </span>
                <button className="btn btn-ghost btn-sm" onClick={() => setView('test-rides')}>
                  Tous <ChevronRight size={14} />
                </button>
              </div>
              <div className="card-body">
                {essaisAujourdhui.length === 0 ? (
                  <p className="text-[var(--text3)] text-sm text-center py-4">Aucun essai programmé</p>
                ) : (
                  <div className="space-y-2">
                    {essaisAujourdhui.slice(0, 4).map(ride => (
                      <div key={ride.id} className="p-3 rounded-lg bg-[var(--bg2)] border border-[var(--border)]">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="font-medium text-sm">{ride.prospect_prenom} {ride.prospect_nom}</div>
                            <div className="text-xs text-[var(--text3)]">
                              {ride.date_rdv?.split('T')[1]?.substring(0, 5) || '—'}
                            </div>
                          </div>
                          <span className={`badge ${
                            ride.statut === 'termine' ? 'badge-green' :
                            ride.statut === 'en_cours' ? 'badge-blue' :
                            'badge-yellow'
                          }`}>
                            {ride.statut}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* ============ GESTION PERSONNEL WIDGETS ============ */}
        {hasPersonnel && !hasSAV && (
          <div className="card">
            <div className="card-header">
              <span className="card-title flex items-center gap-2">
                <Users size={16} style={{ color: 'var(--accent)' }} />
                Équipe
              </span>
              <button className="btn btn-ghost btn-sm" onClick={() => setView('equipe')}>
                Détails <ChevronRight size={14} />
              </button>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-[var(--bg2)] rounded-lg">
                  <div className="text-2xl font-bold">{techniciens.length}</div>
                  <div className="text-xs text-[var(--text3)]">Collaborateurs</div>
                </div>
                <div className="text-center p-4 bg-[var(--bg2)] rounded-lg">
                  <div className="text-2xl font-bold" style={{ color: 'var(--orange)' }}>
                    {(absences || []).filter(a => {
                      const now = new Date();
                      const start = new Date(a.date_debut);
                      const end = new Date(a.date_fin);
                      return start <= now && end >= now;
                    }).length}
                  </div>
                  <div className="text-xs text-[var(--text3)]">Absents aujourd'hui</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============ BEXIO SYNC WIDGETS ============ */}
        {hasBexio && (
          <div className="card">
            <div className="card-header">
              <span className="card-title flex items-center gap-2">
                <RefreshCw size={16} style={{ color: bexioStatus?.connected ? 'var(--green)' : 'var(--red)' }} />
                Bexio Sync
              </span>
              <button className="btn btn-ghost btn-sm" onClick={() => setView('bexio')}>
                Gérer <ChevronRight size={14} />
              </button>
            </div>
            <div className="card-body">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-[var(--bg2)]">
                <div className={`w-3 h-3 rounded-full ${bexioStatus?.connected ? 'bg-green-500' : 'bg-red-500'}`} />
                <div>
                  <div className="font-medium text-sm">
                    {bexioStatus?.connected ? 'Connecté' : 'Déconnecté'}
                  </div>
                  <div className="text-xs text-[var(--text3)]">
                    {bexioStatus?.last_sync ? `Dernière sync: ${fmtDate(bexioStatus.last_sync)}` : 'Jamais synchronisé'}
                  </div>
                </div>
              </div>
              {bexioStatus?.connected && (
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <div className="text-center p-2 bg-[var(--bg3)] rounded">
                    <div className="font-mono font-bold">{bexioStatus?.contacts_count || 0}</div>
                    <div className="text-xs text-[var(--text3)]">Contacts</div>
                  </div>
                  <div className="text-center p-2 bg-[var(--bg3)] rounded">
                    <div className="font-mono font-bold">{bexioStatus?.invoices_count || 0}</div>
                    <div className="text-xs text-[var(--text3)]">Factures</div>
                  </div>
                  <div className="text-center p-2 bg-[var(--bg3)] rounded">
                    <div className="font-mono font-bold">{bexioStatus?.offers_count || 0}</div>
                    <div className="text-xs text-[var(--text3)]">Offres</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Message modules */}
      <div className="mt-6 p-4 rounded-lg bg-[var(--bg2)] border border-[var(--border)]">
        <div className="flex items-center gap-2 text-sm text-[var(--text3)]">
          <span>Modules actifs:</span>
          {activeModules.map(mod => (
            <span key={mod} className="badge badge-green text-xs">{mod}</span>
          ))}
          {license?.days_remaining && license.days_remaining <= 30 && (
            <span className="ml-auto text-xs" style={{ color: license.days_remaining <= 7 ? 'var(--red)' : 'var(--orange)' }}>
              Licence expire dans {license.days_remaining} jours
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdaptiveDashboard;
